import React, { FC, useEffect, useState } from "react";
import { Typography, Button, Space, Popconfirm } from "antd";
import {
  ShoppingCartOutlined,
  EditOutlined,
  DeleteOutlined,
  StarFilled,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { PerfumeResponse } from "../../types/types";
import { ACCOUNT_ADMIN_EDIT } from "../../constants/routeConstants";
import { useSelector } from "react-redux";
import { selectUserFromUserState } from "../../redux-toolkit/user/user-selector";
import "./PerfumeCard.css";

const { Text } = Typography;

type PropsType = {
  perfume: PerfumeResponse;
  isAdminView?: boolean;
  onDelete?: (perfume: PerfumeResponse) => void;
};

const PerfumeCard: FC<PropsType> = ({ perfume, isAdminView = false, onDelete }) => {
  const user    = useSelector(selectUserFromUserState);
  const isAdmin = user?.roles?.includes("ADMIN");

  const [isDistributor, setIsDistributor] = useState(false);

  useEffect(() => {
    setIsDistributor(localStorage.getItem("role") === "3");
  }, []);

  const displayPrice =
    isDistributor && perfume.distributorPrice != null
      ? Number(perfume.distributorPrice)
      : Number(perfume.price);

  const mrp = perfume.actualPrice != null ? Number(perfume.actualPrice) : null;

  const hasDiscount = mrp !== null && mrp > displayPrice;
  const discountPct = hasDiscount
    ? Math.round(((mrp! - displayPrice) / mrp!) * 100)
    : 0;

  return (
    <div className="fk-card">
      <Link to={`/product/${perfume.id}`} className="fk-card-link">

        {/* Image */}
        <div className="fk-card-img-wrap">
          <img
            src={perfume.filename || "/no-image.png"}
            alt={perfume.perfumeTitle}
            className="fk-card-img"
          />
          {perfume.perfumeRating != null && (
            <span className="fk-card-rating">
              {perfume.perfumeRating.toFixed(1)} <StarFilled />
            </span>
          )}
          {discountPct > 0 && (
            <span className="fk-card-discount">{discountPct}% off</span>
          )}
        </div>

        {/* Info */}
        <div className="fk-card-body">
          <div className="fk-card-info">
            <div className="fk-card-title" title={perfume.perfumeTitle}>
              {perfume.perfumeTitle}
            </div>
            <div className="fk-card-brand">{perfume.brand}</div>

            {/* Price */}
            <div className="fk-card-price-row">
              <span className="fk-card-price">₹{displayPrice}</span>
              {hasDiscount && (
                <span className="fk-card-mrp">₹{mrp}</span>
              )}
            </div>

            {perfume.minQuantity != null && (
              <div className="fk-card-min-qty">Min Qty: {perfume.minQuantity}</div>
            )}
          </div>

          {/* Cart row always at bottom */}
          <div className="fk-card-cart-row">
            <ShoppingCartOutlined className="fk-card-cart-icon" />
            <span className="fk-card-cart-label">Add to Cart</span>
          </div>
        </div>
      </Link>

      {/* Admin actions */}
      {isAdmin && isAdminView && (
        <div className="fk-card-admin-bar">
          <Link to={`${ACCOUNT_ADMIN_EDIT}/${perfume.id}`}>
            <Button icon={<EditOutlined />} size="small" type="primary">
              Edit
            </Button>
          </Link>
          <Popconfirm
            title="Delete this product?"
            onConfirm={() => onDelete?.(perfume)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </div>
      )}
    </div>
  );
};

export default PerfumeCard;